// Pure data layer of the guided first run of Reservas de mesas: initial data, validations and the
// FormData of each request. Create uses the same builder as the bar form (buildBarFormData);
// every optional step updates through /bars/update sending only its own fields (the backend
// update is partial: absent fields keep their value).
import { buildBarFormData } from "../barFormData";
import { COUNTRIES } from "../../../pages/reservas/components/ReservaShared";
import { sinSenasEnBar } from "../../../helpers/senasOrganizer";
import { recordatoriosDeVenue } from "../../recordatorios/recordatorios";

export const MAX_GALERIA = 5;

// Same lists as the bar form (ReservasOrganizer): kept here so the flow offers identical choices.
export const DURACIONES_TURNO = [
	{ value: 60, label: "60 min" },
	{ value: 90, label: "90 min" },
	{ value: 120, label: "2 horas" },
	{ value: 150, label: "2:30 hs" },
	{ value: 180, label: "3 horas" },
	{ value: 240, label: "4 horas" },
];
export const VENTANA_OPCIONES = Array.from({ length: 30 }, (_, i) => ({
	value: i + 1,
	label: `${i + 1} ${i === 0 ? "día" : "días"}`,
}));
export const ANTELACION_OPCIONES = [
	{ value: 30, label: "30 min" },
	{ value: 60, label: "1 hora" },
	{ value: 120, label: "2 horas" },
	{ value: 240, label: "4 horas" },
	{ value: 480, label: "8 horas" },
	{ value: 1440, label: "1 día" },
];
export const TOLERANCIA_OPCIONES = [5, 10, 15, 20, 30].map((v) => ({
	value: v,
	label: `${v} min`,
}));
export const CANCELACION_OPCIONES = [
	{ value: 1, label: "1 hora antes" },
	{ value: 2, label: "2 horas antes" },
	{ value: 6, label: "6 horas antes" },
	{ value: 12, label: "12 horas antes" },
	{ value: 24, label: "24 horas antes" },
	{ value: 48, label: "48 horas antes" },
];

// weekday uses the backend convention (0 = domingo); shown Monday first.
export const DIAS_SEMANA = [
	{ weekday: 1, corto: "Lu", largo: "Lunes" },
	{ weekday: 2, corto: "Ma", largo: "Martes" },
	{ weekday: 3, corto: "Mi", largo: "Miércoles" },
	{ weekday: 4, corto: "Ju", largo: "Jueves" },
	{ weekday: 5, corto: "Vi", largo: "Viernes" },
	{ weekday: 6, corto: "Sá", largo: "Sábado" },
	{ weekday: 0, corto: "Do", largo: "Domingo" },
];

export const num = (v) => (v === "" || v == null ? 0 : Number(v) || 0);
const hhmmAMin = (h) => {
	const [a, b] = String(h || "")
		.split(":")
		.map(Number);
	return a * 60 + b;
};

/** Slot start times offered in one day (closing before opening means it closes after midnight). */
export const turnosDe = (desde, hasta, minutos, max = 8) => {
	if (!desde || !hasta || !(minutos > 0)) return [];
	const ini = hhmmAMin(desde);
	let fin = hhmmAMin(hasta);
	if (Number.isNaN(ini) || Number.isNaN(fin)) return [];
	if (fin <= ini) fin += 24 * 60;
	const out = [];
	for (let t = ini; t + minutos <= fin && out.length < max; t += minutos) {
		out.push(
			`${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`,
		);
	}
	return out;
};

export const nuevaZona = () => ({ nombre: "", mesas: "2", cap: "4", sena: "" });

/** Starting values; the name comes from the portal negocio and stays editable. */
export const datosInicialesBar = (negocio = "") => ({
	nombre: negocio,
	nombreDelPortal: negocio,
	onlineBookingEnabled: false,
	stateid: "",
	cityid: "",
	address: "",
	lat: "",
	lng: "",
	dias: [1, 2, 3, 4, 5, 6],
	desde: "12:00",
	hasta: "00:00",
	turno: 120,
	ventana: 6,
	zonas: [
		{ nombre: "Salón", mesas: "8", cap: "4", sena: "" },
		{ nombre: "Vereda", mesas: "4", cap: "2", sena: "" },
	],
	modo: "libre", // "libre" | "sena"
	sena: "",
	maxPersonas: "10",
	zonePricing: false,
	// optional steps
	descripcion: "",
	prefijo: COUNTRIES[0].prefix,
	telefono: "",
	email: "",
	web: "",
	instagram: "",
	portada: null,
	galeria: [],
	antelacion: 120,
	tolerancia: 15,
	cancela: true,
	plazo: 24,
	notas: "",
});

export const coordValida = (v) =>
	v !== "" && v != null && Number.isFinite(Number(v));

export const faltaUbicacion = (d) =>
	!d.stateid
		? "Elegí la provincia"
		: !d.cityid
			? "Elegí la ciudad"
			: !d.address.trim()
				? "Falta la dirección"
				: !coordValida(d.lat) || !coordValida(d.lng)
					? "Confirmá la ubicación en el mapa"
					: "";

export const faltaBar = (d) =>
	!d.nombre.trim() ? "Falta el nombre" : faltaUbicacion(d);

export const faltaHorarios = (d) =>
	!d.dias.length
		? "Elegí al menos un día"
		: !turnosDe(d.desde, d.hasta, d.turno).length
			? "El horario no alcanza para un turno"
			: "";

export const faltaSalon = (d) => {
	if (!d.zonas.length) return "Agregá al menos una zona";
	if (d.zonas.some((z) => !z.nombre.trim())) return "Hay una zona sin nombre";
	if (d.zonas.some((z) => !(num(z.mesas) > 0)))
		return "Cada zona necesita al menos una mesa";
	if (d.zonas.some((z) => !(num(z.cap) > 0)))
		return "Falta la cantidad de personas por mesa";
	return "";
};

export const faltaReserva = (d) =>
	d.modo === "sena" && !(num(d.sena) > 0)
		? "Falta el monto de la seña"
		: !(num(d.maxPersonas) > 0)
			? "Falta el máximo de personas"
			: "";

export const totalesSalon = (zonas) => ({
	mesas: zonas.reduce((a, z) => a + num(z.mesas), 0),
	lugares: zonas.reduce((a, z) => a + num(z.mesas) * num(z.cap), 0),
});

/* ---- contact validation: same rules as the bar form's handleSave ---- */
export const paisPorPrefijo = (prefijo) =>
	COUNTRIES.find((c) => c.prefix === prefijo) || COUNTRIES[0];
export const telefonoCompleto = (d) =>
	d.telefono.trim() ? `${d.prefijo} ${d.telefono.trim()}` : "";

export const faltaContacto = (d) => {
	if (d.telefono.trim() && !paisPorPrefijo(d.prefijo).validate(d.telefono))
		return "Este número no es válido";
	if (d.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim()))
		return "Revisá el email";
	if (d.web.trim() && !/^https?:\/\/.+\..+/.test(d.web.trim()))
		return "El sitio web debe comenzar con http:// o https://";
	if (
		d.instagram.trim() &&
		!/^[a-zA-Z0-9_.]{1,30}$/.test(d.instagram.trim().replace(/^@/, ""))
	) {
		return "Instagram: solo letras, números, puntos y guiones bajos";
	}
	return "";
};

export const faltaSenaZona = (d) =>
	d.zonePricing && d.zonas.some((z) => num(z.sena) < 0)
		? "Una seña no puede ser negativa"
		: "";

/* ---- requests ---- */

export const horariosDe = (d) =>
	DIAS_SEMANA.map(({ weekday }) => ({
		weekday,
		enabled: d.dias.includes(weekday),
		open: d.desde,
		close: d.hasta,
	}));

const zonasDe = (d, conPrecios) =>
	d.zonas.map((z) => {
		const price = conPrecios ? num(z.sena) : 0;
		return {
			name: z.nombre.trim(),
			tableCount: num(z.mesas),
			tableCapacity: num(z.cap),
			price,
			priceEnabled: conPrecios && price > 0,
		};
	});

/**
 * What a copied bar (`d.importado`, see importarConfig) adds to the create request: contact, policies,
 * amenities, reminders and the zone prices. Tables regenerate from the zones, so nothing goes after.
 */
const camposImportadosBar = (d, conPrecios) => ({
	shortDescription: d.importado.shortDescription || "",
	description: d.descripcion.trim(),
	contactPhone: telefonoCompleto(d),
	contactEmail: d.email.trim(),
	website: d.web.trim(),
	instagram: d.instagram.trim(),
	minAdvanceMinutes: d.antelacion,
	toleranceMinutes: d.tolerancia,
	cancellationAllowed: Boolean(d.cancela),
	cancellationPolicyHours: d.plazo,
	additionalNotes: d.notas.trim(),
	amenitiesText: (d.importado.amenities || []).join(", "),
	zonePricingEnabled: conPrecios,
	zones: zonasDe(d, conPrecios),
	...recordatoriosDeVenue(d.importado.reminders),
});

/**
 * The form state buildBarFormData reads for the create request. A free bar always travels with
 * no deposit and no zone pricing (the backend answers 403 otherwise); `soloGratis` forces that
 * for accounts that cannot charge yet.
 */
export const formularioCrearBar = (d, { soloGratis = false } = {}) => {
	const form = {
		barId: "",
		name: d.nombre.trim(),
		address: d.address.trim(),
		lat: d.lat,
		lng: d.lng,
		cityid: d.cityid,
		stateid: d.stateid,
		reservationMode: d.modo === "sena" ? "deposit" : "free",
		depositAmount: d.modo === "sena" ? num(d.sena) : 0,
		slotDurationMinutes: d.turno,
		reservationWindowDays: d.ventana,
		maxPartySize: num(d.maxPersonas),
		cancellationPolicyHours: 24,
		isFeatured: false,
		amenitiesText: "",
		openingHours: horariosDe(d),
		zones: zonasDe(d, false),
		zonePricingEnabled: false,
		minAdvanceMinutes: 120,
		cancellationAllowed: true,
		toleranceMinutes: 15,
		contactPhone: "",
		contactEmail: "",
		additionalNotes: "",
		existingGallery: [],
		galleryFiles: [],
	};
	const completo = d.importado ? { ...form, ...camposImportadosBar(d, !soloGratis && d.modo === "sena" && Boolean(d.zonePricing)) } : form;
	return soloGratis || d.modo !== "sena" ? sinSenasEnBar(completo) : completo;
};

export const formDataCrearBar = (d, opts) => {
	const fd = buildBarFormData(formularioCrearBar(d, opts));
	fd.append("onlineBookingEnabled", String(d.onlineBookingEnabled === true));
	return fd;
};

const base = (barId) => {
	const fd = new FormData();
	fd.append("barId", barId);
	return fd;
};

export const formDataContacto = (barId, d) => {
	const fd = base(barId);
	fd.append("description", d.descripcion.trim());
	fd.append("contactPhone", telefonoCompleto(d));
	fd.append("contactEmail", d.email.trim());
	fd.append("website", d.web.trim());
	fd.append("instagram", d.instagram.trim());
	return fd;
};

export const hayFotos = (d) => Boolean(d.portada) || d.galeria.length > 0;

// No existingGallery on purpose: the backend then keeps what is there and appends (max 5).
export const formDataFotos = (barId, d) => {
	const fd = base(barId);
	if (d.portada) fd.append("image", d.portada);
	d.galeria.slice(0, MAX_GALERIA).forEach((f) => fd.append("galleryimage", f));
	return fd;
};

export const formDataPoliticas = (barId, d) => {
	const fd = base(barId);
	fd.append("minAdvanceMinutes", String(d.antelacion));
	fd.append("toleranceMinutes", String(d.tolerancia));
	fd.append("cancellationAllowed", String(Boolean(d.cancela)));
	fd.append("cancellationPolicyHours", String(d.plazo));
	fd.append("additionalNotes", d.notas.trim());
	return fd;
};

// A zone has its own price only when it was given one; the rest use the general deposit.
export const formDataSenaZona = (barId, d) => {
	const fd = base(barId);
	fd.append("zonePricingEnabled", String(Boolean(d.zonePricing)));
	fd.append("zones", JSON.stringify(zonasDe(d, Boolean(d.zonePricing))));
	return fd;
};
